import type { Crop, Variety } from '@protector/entities/crop/models';
import type { FieldSeason } from '@protector/entities/property/models';
import { RegionType } from '@protector/entities/region/constants';
import type {
  AreaInfo,
  BuildAreaInfoBySeasonFieldProps,
  Field,
  GeometryProperties,
  GetVarietiesNamesFromSeasonFieldByRegionSeasonsParams,
  Region,
  V1Region
} from '@protector/entities/region/models';
import type { Season, SeasonFlat } from '@protector/entities/season/models';
import { Threshold } from '@protector/utils/constants';
import type { Dictionary, Indicator, ListMultimap, Nullable, UUID } from '@protector/utils/types';
import type { AreaWithoutGeometryFeatures } from 'core/shared/enums/featureFlags.enum';
import dayjs from 'dayjs';
import type { Feature, GeoJsonObject, Geometry, Polygon } from 'geojson';
import _, { isEmpty } from 'lodash';
import type { AnnotationStage, Monitoring, Sample } from 'pages/timeline/timeline.models';
import { hashNumber } from '../../core/utils/functions';

/**
 * Build Regions recursive. The children are filled until be fields not abstracts.
 * @param parentRegion {Region} Parent region to fill children.
 * @param fieldsByParentRegion {ListMultimap<Field>} Fields segregated by parent region.
 * @param childrenByParent {ListMultimap<Region>} Regions segregated by parent region.
 * @param seasonFieldsById {Dictionary<SeasonField>} Season Fields by id.
 * @param cropsById Crops by id.
 * @param varietiesById Varieties by id.
 * @param seasons {SeasonFlat[]} Seasons.
 */

export const getSeasonFieldReferenceDate = (endsAt?: string, propertyTimeZone?: string): string => {
  const today = (propertyTimeZone ? dayjs().tz(propertyTimeZone) : dayjs()).format('YYYY-MM-DD');
  if (!endsAt) {
    return today;
  }
  const seasonFieldEndDate = dayjs.utc(endsAt);
  if (!seasonFieldEndDate.isValid()) {
    return today;
  }
  if (seasonFieldEndDate.isAfter()) {
    return today;
  }
  return propertyTimeZone ? seasonFieldEndDate.tz(propertyTimeZone).format('YYYY-MM-DD') : seasonFieldEndDate.format('YYYY-MM-DD');
};

const buildRegion = (
  parentRegion: Region,
  fieldsByParentRegion: ListMultimap<Field>,
  childrenByParent: ListMultimap<Region>,
  seasonFieldsById: Dictionary<FieldSeason>,
  seasons: Season[],
  cropsById: Dictionary<Crop>,
  varietiesById: Dictionary<Variety>
): Region => {
  const regionsByParent = childrenByParent[parentRegion.id] || [];
  const fieldsByParent = fieldsByParentRegion[parentRegion.id] || [];
  const fields = fieldsByParent.map(field =>
    buildRegionAreaFromField(field, seasonFieldsById[field.id], seasons, cropsById, varietiesById)
  );
  const regions = regionsByParent.map(c =>
    buildRegion(c, fieldsByParentRegion, childrenByParent, seasonFieldsById, seasons, cropsById, varietiesById)
  );
  const seasonsByFields = fields.flatMap(f => f.seasons);
  const seasonsByRegions = regions.flatMap(r => r.seasons);

  return {
    ...parentRegion,
    children: [...fields, ...regions] as Region[],
    seasons: [...new Map([...seasonsByFields, ...seasonsByRegions].map(variety => [variety.id, variety])).values()] as Season[]
  };
};

export const buildFieldsByParentRegionId = (fields: Field[]): Dictionary<Field[]> => {
  const fieldsByParentRegion = _.groupBy(fields, 'parent_region_id');

  const parentRegionIds = Object.keys(fieldsByParentRegion);
  if (parentRegionIds.length === 2 && parentRegionIds.includes('null')) {
    const parentId = parentRegionIds.find(id => id !== 'null');

    if (parentId) {
      const regionsWithoutParentRegionIdRefactored = fieldsByParentRegion.null.map(region => ({ ...region, parent_region_id: parentId }));

      fieldsByParentRegion[parentId].push(...regionsWithoutParentRegionIdRefactored);

      delete fieldsByParentRegion.null;
    }
  }

  return fieldsByParentRegion;
};

/**
 * Build RegionTree. The return is similar to the return of endpoint v1/panel/properties/{property_id}/regions/{region_id}.
 * Use the fields, regions, seasons and crops to build a region tree. Is needed a root region.
 * @param abstractRegions {V1Region[]} Abstract regions. Fields are not included here.
 * @param seasonFields {SeasonField[]} Relationship of Season-Field.
 * @param fields {Field[]} Fields definitions.
 * @param seasons {SeasonFlat[]} Seasons.
 * @param crops {Crop[]} Crops of Seasons.
 * @param varieties {Variety[]} Varieties of fields.
 */
export const buildDeepRegion = (
  abstractRegions: V1Region[],
  seasonFields: FieldSeason[],
  fields: Field[],
  seasons: Season[],
  crops: Crop[] = [],
  varieties: Variety[] = []
): Region => {
  let rootRegionOptional: V1Region | undefined;

  const childRegionsByParentRegion = abstractRegions.reduce((accumulator, region) => {
    const { parentId } = region;

    if (!parentId) {
      rootRegionOptional = region;
    } else {
      if (!accumulator[parentId]) {
        accumulator[parentId] = [];
      }
      accumulator[parentId].push(buildRegionFromV1Region(region));
    }

    return accumulator;
  }, {} as ListMultimap<Region>);
  if (!rootRegionOptional) {
    throw Error('Missing a root region!');
  }

  const rootRegion: Region = buildRegionFromV1Region(rootRegionOptional);
  const seasonFieldsById: Dictionary<FieldSeason> = _.mapKeys(seasonFields, 'areaId');
  const varietiesById = _.mapKeys(varieties, 'id');
  const cropsById = _.mapKeys(crops, 'id');
  const fieldsByParentRegion: ListMultimap<Field> = buildFieldsByParentRegionId(fields);

  return buildRegion(rootRegion, fieldsByParentRegion, childRegionsByParentRegion, seasonFieldsById, seasons, cropsById, varietiesById);
};

export const getFormattedDate = (date?: string | null): string | undefined => {
  if (!date) return '';

  return dayjs(date).format('L');
};

const getCropNameFromSeasonFieldBySeasons = (seasonId?: string, seasons?: Dictionary<Season>) => {
  if (!seasonId || !seasons || isEmpty(seasons)) return;

  return seasons[seasonId]?.crop.name;
};

const getVarietiesNamesFromSeasonFieldByRegionSeasons = ({
  seasonField,
  varieties,
  seasons
}: GetVarietiesNamesFromSeasonFieldByRegionSeasonsParams) => {
  if (!seasonField || !seasons || isEmpty(seasons) || !varieties) return;

  const seasonFromSeasonField = !!seasonField.season_id && seasons[seasonField.season_id];
  const varietiesIds = seasonField.varieties_ids;

  if (!varietiesIds?.length || !seasonFromSeasonField) return;

  const cropId = seasonFromSeasonField.crop.id;

  const varietiesNames = varietiesIds.reduce((acc, varietyId) => {
    const cropName = varieties[cropId]?.find(variety => variety.id === varietyId)?.name;

    if (cropName) {
      acc.push(cropName);
    }

    return acc;
  }, [] as string[]);

  return varietiesNames.join(', ');
};

export const buildAreaInfoBySeasonField = ({
  regionChildrenMapping,
  areaSeasonFieldMap,
  varieties,
  seasons,
  region
}: BuildAreaInfoBySeasonFieldProps): AreaInfo | undefined => {
  if (!region || !regionChildrenMapping || !areaSeasonFieldMap) return;

  const children = regionChildrenMapping?.[region.id];

  if (!children) {
    const seasonField = areaSeasonFieldMap[region.id];

    if (seasonField) {
      return {
        variety: getVarietiesNamesFromSeasonFieldByRegionSeasons({ seasonField, seasons, varieties }),
        crop: getCropNameFromSeasonFieldBySeasons(seasonField.season_id, seasons),
        emergency_date: seasonField.emergency_date,
        harvest_date: seasonField.harvesting_date,
        planting_date: seasonField.planting_date,
        area: region.total_area,
        children: undefined,
        key: seasonField.id,
        name: region.name,
        areaVariables: []
      };
    }
  }

  return {
    area: 0,
    key: region.id,
    name: region.name,
    children: children?.map(
      childRegion =>
        buildAreaInfoBySeasonField({
          regionChildrenMapping,
          region: childRegion,
          areaSeasonFieldMap,
          varieties,
          seasons
        })!
    )
  };
};

export const castToFourDecimalPlaces = (areaInfo: AreaInfo): AreaInfo => {
  const children = areaInfo.children;

  const childrenWithFourDecimalPlaces = children?.map(castToFourDecimalPlaces);
  const areaWithFourDecimalPlaces = +areaInfo.area.toFixed(4);

  return { ...areaInfo, children: childrenWithFourDecimalPlaces, area: areaWithFourDecimalPlaces };
};

export const fillAreaValues = (areaInfo: AreaInfo): AreaInfo => {
  const children = areaInfo.children;
  if (!children) return areaInfo;

  const childrenWithFilledValues = children.map(child => fillAreaValues(child));
  const totalArea = childrenWithFilledValues.map(child => child.area).reduce<number>((a, b) => a + b, 0) ?? 0;

  return { ...areaInfo, children: childrenWithFilledValues, area: totalArea };
};

const buildRegionFromV1Region = (v1Region: V1Region) => {
  return {
    children: [],
    id: v1Region.id,
    name: v1Region.name,
    parent_id: v1Region.parentId,
    seasons: [],
    type: RegionType.REGION,
    total_area: 0
  } as unknown as Region;
};

const buildRegionAreaFromField = (
  field: Field,
  seasonField: FieldSeason,
  seasons: (Season | SeasonFlat)[],
  cropsById: Dictionary<Crop>,
  varietiesById: Dictionary<Variety>
): Region => {
  if (seasonField) {
    const fieldSeasons = seasons.reduce((accumulator, season) => {
      if (season.id !== seasonField.seasonId) {
        return accumulator;
      }

      accumulator.push({
        ...season,
        crop: (season as Season)?.crop || cropsById[(season as Season).crop?.id],
        crop_id: (season as Season).crop?.id
      } as Season);
      return accumulator;
    }, [] as Season[]);

    const varieties =
      seasonField?.varieties?.reduce((accumulator, varietyId) => {
        if (varietiesById[varietyId]) {
          accumulator.push(varietiesById[varietyId]);
        }
        return accumulator;
      }, [] as Variety[]) || [];

    const archived = field.archived === true;
    const futureSeason = !!seasonField.startsAt && dayjs().isBefore(dayjs(seasonField.startsAt));

    return {
      geometry: {
        geometry: field.geometry,
        type: 'Feature',
        id: hashNumber(field.id),
        properties: {
          name: field.name,
          id: field.id,
          is_archived: archived,
          valid_since: field.valid_since,
          valid_until: field.valid_until
        }
      } as unknown as Feature<Geometry, GeometryProperties>,
      id: field.id,
      name: field.name,
      parent_id: field.parent_region_id,
      seasons: fieldSeasons,
      type: RegionType.AREA,
      children: [],
      total_area: field.declared_area,
      calculated_area: field.calculated_area,
      varieties,
      json_extended_attributes: field.json_extended_attributes,
      valid_since: field.valid_since,
      valid_until: field.valid_until,
      is_archived: archived,
      ...(futureSeason && {
        current_info: {
          area_in_hectares: field.calculated_area,
          severity_level: null,
          crop_future: true,
          crops: fieldSeasons.map(season => season.crop).filter((crop): crop is Crop => !!crop)
        }
      }),
      ...(archived && {
        current_info: { area_in_hectares: field.calculated_area, severity_level: null, crop_ended: true }
      })
    };
  }
  throw Error('There is not a season field for this field!');
};

/**
 * Get the fields from region tree recursively. The children must be normalized.
 * @param rootRegion Parent region
 * @param regions Dict of regions to iterate
 * @returns childrenIds
 */
const getFieldsIdsRecursive = (rootRegion: Region, regions: Dictionary<Region>): UUID[] => {
  const childrenIds = (rootRegion?.children as UUID[]) || [];
  const children = childrenIds.map((childId: UUID) => regions[childId]).filter(r => !!r);
  return [
    ...new Set([
      ...children.filter(child => child.type === RegionType.AREA).map(c => c.id),
      ...children.filter(child => child.type === RegionType.REGION).flatMap(region => getFieldsIdsRecursive(region, regions))
    ])
  ];
};

const getFieldsIdsChildrens = (rootRegion: Nullable<Region>, regions: Dictionary<Region>): UUID[] => {
  if (!rootRegion) return [];
  const childrenIds = (rootRegion.children as UUID[]) || [];
  const children = childrenIds.map((childId: UUID) => regions[childId]).filter(r => !!r);
  return [...children.filter(child => child.type === RegionType.AREA).map(c => c.id)];
};

const hasGeometry = (feature: GeoJsonObject): boolean => {
  if (feature.type === 'FeatureCollection') {
    return (feature as GeoJSON.FeatureCollection).features.some((childFeature: GeoJsonObject) => {
      return hasGeometry(childFeature);
    });
  }

  const areaGeometry = feature as GeoJSON.Feature<Polygon>;
  if (areaGeometry?.geometry?.coordinates?.length) {
    return true;
  }
  return false;
};

export const getFieldIdsByEndDateInSeasonField = (fieldSeasons: FieldSeason[], propertyTimeZone?: string) => {
  return fieldSeasons.reduce<Record<string, string[]>>((accumulator, seasonField) => {
    const mutableAccumulator = accumulator;
    const referenceDate = getSeasonFieldReferenceDate(seasonField.endsAt, propertyTimeZone);

    if (!mutableAccumulator[referenceDate]) {
      mutableAccumulator[referenceDate] = [];
    }

    mutableAccumulator[referenceDate].push(seasonField.areaId);

    return mutableAccumulator;
  }, {});
};

const regionHasGeometry = (region: Region) => {
  if (region.type === RegionType.AREA) {
    if (region.geometry) {
      return hasGeometry(region.geometry);
    }
  }
  return true;
};

const getFieldsWithGeometryArray = (
  regions: Region[],
  flags: Dictionary<boolean | string> | null = null,
  source: AreaWithoutGeometryFeatures | null = null
): Region[] => {
  const hasSource = !source || flags?.AreasWithoutGeometryFlags?.toString()?.includes(source);
  return hasSource ? regions.filter(regionHasGeometry) : regions;
};

export const getFieldsWithGeometry = (
  regions: Dictionary<Region>,
  flags: Dictionary<boolean | string> | null = null,
  source: AreaWithoutGeometryFeatures | null = null
): Dictionary<Region> => {
  const hasSource = !source || flags?.AreasWithoutGeometryFlags?.toString()?.includes(source);
  if (hasSource) {
    const filteredRegions = getFieldsWithGeometryArray(Object.values(regions));
    return filteredRegions.reduce((acc, region) => {
      acc[region.id] = region;
      return acc;
    }, {} as Dictionary<Region>);
  }

  return regions;
};

export const sortSamples = (monitoring: Monitoring): Monitoring => {
  // Sort Indicators
  // Just sort by absolute value

  const compareIndicators = (a: Indicator, b: Indicator) => {
    const thresholdValues = (status: Indicator['status']) =>
      ({
        [Threshold.DAMAGE]: 3,
        [Threshold.CONTROL]: 2,
        [Threshold.ACCEPTANCE]: 1,
        [Threshold.NONE]: 0
      }[status || Threshold.NONE] ?? 0);

    const statusA = thresholdValues(a.status);
    const statusB = thresholdValues(b.status);

    if (statusA > statusB) return -1;
    if (statusB > statusA) return 1;

    if (a.double_value < b.double_value || a.value < b.value) {
      return 1;
    }
    if (a.double_value > b.double_value || a.value > b.value) {
      return -1;
    }

    return a.name.localeCompare(b.name);
  };
  // Sort Phenomena
  const compareSamples = (a: Sample, b: Sample) => {
    // Get state and max value of a phenomenon
    // state could be: 1 - Acceptance / 2 - Control / 3 - Damage
    const getPriorities = (sample: Sample) => {
      // Starts with Acceptance as state and 0 as max value
      let state = 1;
      let maxValue = 0;
      sample.phenomenon.indicators.forEach((indicator: Indicator) => {
        // If this phenomenon isn't already marked as "Damage" but the current indicator is in "Control"
        if (indicator.status === 'CONTROL' && state < 3) {
          state = 2;
        }
        if (indicator.status === 'DAMAGE') {
          state = 3;
        }
        // This maybe isn't necessary, because indicators are sorted
        if (indicator.value > maxValue || indicator.double_value > maxValue) {
          maxValue = indicator.value ? indicator.value : indicator.double_value;
        }
      });
      return [state, maxValue];
    };
    const [aState, aMaxValue] = getPriorities(a);
    const [bState, bMaxValue] = getPriorities(b);
    // If they're in the same state, sort by value, otherwise sort by state
    // "Damage" < "Control" < "Acceptance"
    if (aState === bState) {
      if (aMaxValue < bMaxValue) {
        return 1;
      }
      if (aMaxValue > bMaxValue) {
        return -1;
      }
      // If they have the same maxValue, sort by name
      return a.phenomenon.name.localeCompare(b.phenomenon.name);
    }
    if (aState > bState) {
      return -1;
    }
    if (aState < bState) {
      return 1;
    }
    return 0;
  };
  // Sort indicators of each phenomenon
  const samplesWithSortedIndicators = monitoring.samples.map((sample: Sample) => {
    return {
      ...sample,
      phenomenon: {
        ...sample.phenomenon,
        indicators: [...sample.phenomenon.indicators].sort(compareIndicators)
      }
    };
  });
  // Sort samples
  const sortedSamples = [...samplesWithSortedIndicators].sort(compareSamples);
  return {
    ...monitoring,
    samples: sortedSamples
  };
};

export const parsedDataToAnnotations = (annotation: AnnotationStage, flag: boolean): AnnotationStage => {
  return {
    ...annotation,
    ...(flag
      ? {
          medias: annotation.medias
        }
      : {})
  };
};

const RegionsUtil = {
  buildDeepRegion,
  getFieldsIdsRecursive,
  getFieldsIdsChildrens
};

export default RegionsUtil;
